import { getCookie, setCookie, removeCookie } from './cookie';
import moment, { Moment } from 'moment-timezone';
import {
	centsToDollars,
	GA_MEASUREMENT_ID,
	normalizeGclid,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RunnitNodeDef,
	User,
	TrackingPurchaseRevenueItem,
} from '@run-diffusion/shared';
import { functions } from '../firebase';

/*
 * Types for Google Ads click ID capture
 */
export type GoogleAdsClickIds = {
	gclid: string | null
	gbraid: string | null
	wbraid: string | null
	capturedAtMs: number | null
	source: 'url' | 'storage' | 'cookie' | 'none'
};

/*
 * Types for Google Ads session attributes
 */
export type GoogleAdsSessionAttributes = {
	sessionAttributesEncoded: string | null
	capturedAtMs: number | null
	source: 'url' | 'storage' | 'none'
};

const GOOGLE_ADS_CLICK_IDS_STORAGE_KEY = 'rd_google_ads_click_ids_v1';
const SESSION_ATTRS_STORAGE_KEY = 'rd_google_session_attributes_v1';
const SESSION_ATTRS_NONCE_COOKIE = 'rd_session_attrs_nonce_v1';

const safeJsonParse = <T>(value: string | null): T | null => {
	if (!value) return null;
	try {
		return JSON.parse(value) as T;
	} catch {
		return null;
	}
};

/*
 * Captures Google Ads click IDs from URL parameters.
 * Returns the raw gclid/gbraid/wbraid values if present.
 */
export const getGoogleAdsClickIdsFromUrl = (): GoogleAdsClickIds => {
	if (typeof window === 'undefined') {
		return { gclid: null, gbraid: null, wbraid: null, capturedAtMs: null, source: 'none' };
	}

	const params = new URLSearchParams(window.location.search);
	const gclid = normalizeGclid(params.get('gclid'));
	const gbraid = params.get('gbraid');
	const wbraid = params.get('wbraid');

	if (!gclid && !gbraid && !wbraid) {
		return { gclid: null, gbraid: null, wbraid: null, capturedAtMs: null, source: 'none' };
	}

	return {
		gclid,
		gbraid,
		wbraid,
		capturedAtMs: Date.now(),
		source: 'url',
	};
};

/*
 * Gets stored Google Ads click IDs from localStorage.
 */
export const getStoredGoogleAdsClickIds = (): GoogleAdsClickIds => {
	if (typeof window === 'undefined') {
		return { gclid: null, gbraid: null, wbraid: null, capturedAtMs: null, source: 'none' };
	}

	const stored = safeJsonParse<GoogleAdsClickIds>(window.localStorage.getItem(GOOGLE_ADS_CLICK_IDS_STORAGE_KEY));
	if (!stored) {
		return { gclid: null, gbraid: null, wbraid: null, capturedAtMs: null, source: 'none' };
	}

	return {
		gclid: normalizeGclid(stored.gclid || null),
		gbraid: stored.gbraid || null,
		wbraid: stored.wbraid || null,
		capturedAtMs: stored.capturedAtMs || null,
		source: 'storage',
	};
};

/*
 * Persists Google Ads click IDs from URL to localStorage (first-touch wins).
 * Call this once at app boot (e.g., in App.vue created()).
 * Returns the current state of click IDs after potential update.
 */
export const persistGoogleAdsClickIdsFromUrl = (): GoogleAdsClickIds => {
	if (typeof window === 'undefined') {
		return { gclid: null, gbraid: null, wbraid: null, capturedAtMs: null, source: 'none' };
	}

	const fromUrl = getGoogleAdsClickIdsFromUrl();
	if (fromUrl.source !== 'url') return getStoredGoogleAdsClickIds();

	const existing = getStoredGoogleAdsClickIds();
	const merged: GoogleAdsClickIds = {
		gclid: existing.gclid || fromUrl.gclid || null,
		gbraid: existing.gbraid || fromUrl.gbraid || null,
		wbraid: existing.wbraid || fromUrl.wbraid || null,
		capturedAtMs: existing.capturedAtMs || fromUrl.capturedAtMs || Date.now(),
		source: 'url',
	};

	window.localStorage.setItem(GOOGLE_ADS_CLICK_IDS_STORAGE_KEY, JSON.stringify(merged));
	return merged;
};

/**
 * Base64url encodes a string (RFC 4648).
 */
function base64UrlEncode(input: string): string {
	const bytes = new TextEncoder().encode(input);
	let binary = '';
	for (let i = 0; i < bytes.byteLength; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	const base64 = btoa(binary);
	return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Builds the session attributes object and returns it as a base64url encoded JSON string.
 */
export const buildSessionAttributesEncoded = (): string | null => {
	if (typeof window === 'undefined') return null;

	const searchParams = new URLSearchParams(window.location.search);
	const hasAnyAds = searchParams.has('gclid') || searchParams.has('gbraid') || searchParams.has('wbraid');
	const hasAnyGad = Array.from(searchParams.keys()).some(k => k.startsWith('gad_'));

	if (!hasAnyAds && !hasAnyGad) return null;

	const sessionAttrs: Record<string, string> = {};

	// 1. Capture all gad_* params
	searchParams.forEach((value, key) => {
		if (key.startsWith('gad_')) {
			sessionAttrs[key] = value;
		}
	});

	// 2. Add required metadata
	sessionAttrs['session_start_time_usec'] = (Date.now() * 1000).toString();
	sessionAttrs['landing_page_url'] = window.location.href;
	sessionAttrs['landing_page_referrer'] = document.referrer || '';
	sessionAttrs['landing_page_user_agent'] = navigator.userAgent || '';

	return base64UrlEncode(JSON.stringify(sessionAttrs));
};

/**
 * Gets stored session attributes from localStorage.
 */
export const getStoredSessionAttributes = (): GoogleAdsSessionAttributes => {
	if (typeof window === 'undefined') {
		return { sessionAttributesEncoded: null, capturedAtMs: null, source: 'none' };
	}

	const stored = safeJsonParse<GoogleAdsSessionAttributes>(window.localStorage.getItem(SESSION_ATTRS_STORAGE_KEY));
	if (!stored) {
		return { sessionAttributesEncoded: null, capturedAtMs: null, source: 'none' };
	}

	return {
		sessionAttributesEncoded: stored.sessionAttributesEncoded || null,
		capturedAtMs: stored.capturedAtMs || null,
		source: 'storage',
	};
};

/**
 * Persists session attributes from URL to localStorage (first-touch wins).
 */
export const persistSessionAttributesFromUrl = (): GoogleAdsSessionAttributes => {
	if (typeof window === 'undefined') {
		return { sessionAttributesEncoded: null, capturedAtMs: null, source: 'none' };
	}

	const encoded = buildSessionAttributesEncoded();
	if (!encoded) return getStoredSessionAttributes();

	const existing = getStoredSessionAttributes();
	if (existing.sessionAttributesEncoded) return existing;

	const next: GoogleAdsSessionAttributes = {
		sessionAttributesEncoded: encoded,
		capturedAtMs: Date.now(),
		source: 'url',
	};

	window.localStorage.setItem(SESSION_ATTRS_STORAGE_KEY, JSON.stringify(next));
	return next;
};

/**
 * Hydrates localStorage session attributes from the shared cross-domain nonce cookie.
 */
export const hydrateSessionAttributesFromNonce = async (): Promise<void> => {
	if (typeof window === 'undefined') return;

	const nonce = getCookie(SESSION_ATTRS_NONCE_COOKIE);
	if (!nonce) return;

	// Only hydrate if we don't already have session attributes (first-touch wins)
	const existing = getStoredSessionAttributes();
	if (existing.sessionAttributesEncoded) {
		// We already have it, so we can just clear the nonce cookie
		removeCookie(SESSION_ATTRS_NONCE_COOKIE, { domain: '.rundiffusion.com', path: '/' });
		return;
	}

	try {
		const getSessionAttributesByNonceRef = functions.httpsCallable('getSessionAttributesByNonce');
		const result = await getSessionAttributesByNonceRef({ nonce });
		const { sessionAttributesEncoded } = result.data as { sessionAttributesEncoded: string | null };

		if (sessionAttributesEncoded) {
			const next: GoogleAdsSessionAttributes = {
				sessionAttributesEncoded,
				capturedAtMs: Date.now(),
				source: 'none', // source is the bridge
			};
			window.localStorage.setItem(SESSION_ATTRS_STORAGE_KEY, JSON.stringify(next));
		}

		// Clear the nonce cookie after attempted hydration (success or not)
		removeCookie(SESSION_ATTRS_NONCE_COOKIE, { domain: '.rundiffusion.com', path: '/' });
	} catch (e) {
		console.error('Error hydrating session attributes from nonce:', e);
	}
};

/*
 * Gets the raw GCLID from the _gcl_aw cookie (parsed).
 */
const getGclidFromGclAwCookie = (): string | null => {
	const gclAw = getCookie('_gcl_aw') || null;
	return normalizeGclid(gclAw);
};

/*
 * Unified getter for Google Ads click IDs.
 * Priority: URL > localStorage > cookie (raw gclid from _gcl_aw)
 */
export const getGoogleAdsClickIds = (): GoogleAdsClickIds => {
	const fromUrl = getGoogleAdsClickIdsFromUrl();
	if (fromUrl.source === 'url') return fromUrl;

	const fromStorage = getStoredGoogleAdsClickIds();
	if (fromStorage.source === 'storage' && (fromStorage.gclid || fromStorage.gbraid || fromStorage.wbraid)) {
		return fromStorage;
	}

	const cookieGclid = getGclidFromGclAwCookie();
	if (cookieGclid) {
		return { gclid: cookieGclid, gbraid: null, wbraid: null, capturedAtMs: null, source: 'cookie' };
	}

	return { gclid: null, gbraid: null, wbraid: null, capturedAtMs: null, source: 'none' };
};

/**
 * Gets the user's current consent status from Termly's localStorage cache.
 * Maps Termly's 'advertising' category to Google Ads 'ad_user_data' and 'ad_personalization'.
 */
export const getConsentStatus = (): { adUserData: 'GRANTED' | 'DENIED', adPersonalization: 'GRANTED' | 'DENIED' } => {
	try {
		const rawCache = localStorage.getItem('TERMLY_API_CACHE');
		if (!rawCache) return { adUserData: 'DENIED', adPersonalization: 'DENIED' };

		const cache = JSON.parse(rawCache);
		// Accessing the exact path identified in the Termly cache object
		const isAdvertisingGranted = cache?.TERMLY_COOKIE_CONSENT?.value?.advertising === true;

		return {
			adUserData: isAdvertisingGranted ? 'GRANTED' : 'DENIED',
			adPersonalization: isAdvertisingGranted ? 'GRANTED' : 'DENIED',
		};
	} catch (e) {
		console.error('Error parsing Termly consent cache:', e);
		return { adUserData: 'DENIED', adPersonalization: 'DENIED' };
	}
};

/*
 * Cross-Domain Attribution Bridge
 *
 * The shared cookie `rd_attribution_v1` is set by the marketing site (rundiffusion.com)
 * Edge Middleware and scoped to `.rundiffusion.com`. This function reads that cookie
 * and merges its contents into the app's localStorage, bridging attribution data
 * captured before the user navigated to app.rundiffusion.com.
 */

const SHARED_ATTR_COOKIE = 'rd_attribution_v1';

type SharedAttributionV1 = {
	v: 1
	utm?: {
		version: 1
		first: Record<string, { value: string | null; capturedAtMs: number | null }>
		last: Record<string, { value: string | null; capturedAtMs: number | null }>
	}
	ads?: {
		gclid: string | null
		gbraid: string | null
		wbraid: string | null
		capturedAtMs: number | null
	}
	updatedAtMs?: number
};

const safeParseSharedAttributionCookie = (): SharedAttributionV1 | null => {
	try {
		const raw = getCookie(SHARED_ATTR_COOKIE);
		if (!raw) return null;
		const json = decodeURIComponent(raw);
		const parsed = JSON.parse(json) as SharedAttributionV1;
		if (parsed?.v !== 1) return null;
		return parsed;
	} catch {
		return null;
	}
};

/**
 * Hydrates localStorage attribution data from the shared cross-domain cookie.
 * Call this BEFORE persistGoogleAdsClickIdsFromUrl() and persistUtmAttributionFromUrl()
 * so that marketing-site attribution is pulled in first, then any new URL params
 * on the app side can update last-touch values.
 *
 * - Ads click IDs: first-touch wins (only set if localStorage is empty)
 * - UTMs first-touch: set-if-empty per field
 * - UTMs last-touch: choose newest per field (by capturedAtMs)
 */
export const hydrateAttributionFromSharedCookie = (): void => {
	if (typeof window === 'undefined') return;

	const shared = safeParseSharedAttributionCookie();
	if (!shared) return;

	// --- Ads click IDs: first-touch wins ---
	if (shared.ads) {
		const existing = getStoredGoogleAdsClickIds();
		const merged: GoogleAdsClickIds = {
			gclid: existing.gclid || shared.ads.gclid || null,
			gbraid: existing.gbraid || shared.ads.gbraid || null,
			wbraid: existing.wbraid || shared.ads.wbraid || null,
			capturedAtMs: existing.capturedAtMs || shared.ads.capturedAtMs || null,
			source: 'storage',
		};

		window.localStorage.setItem(GOOGLE_ADS_CLICK_IDS_STORAGE_KEY, JSON.stringify(merged));
	}

	// --- UTMs: first-touch set-if-empty, last-touch choose newest per field ---
	if (shared.utm) {
		const existing = getStoredUtmAttributionState();
		const next = {
			version: 1,
			first: { ...existing.first },
			last: { ...existing.last },
		} as UtmAttributionState;

		const utmKeys: UtmKey[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_matchtype', 'utm_device', 'utm_network', 'utm_placement', 'utm_test'];

		utmKeys.forEach((k: UtmKey) => {
			const sharedFirst = shared.utm?.first?.[k];
			const sharedLast = shared.utm?.last?.[k];

			// First-touch: set only if current is empty
			if (!next.first[k]?.value && sharedFirst?.value) {
				next.first[k] = sharedFirst as UtmFieldState;
			}

			// Last-touch: choose the newest by capturedAtMs
			const existingLastMs = next.last[k]?.capturedAtMs || 0;
			const sharedLastMs = sharedLast?.capturedAtMs || 0;

			if (sharedLast?.value && sharedLastMs >= existingLastMs) {
				next.last[k] = sharedLast as UtmFieldState;
			}
		});

		window.localStorage.setItem(UTM_ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
	}
};

/*
 * Types for UTM attribution capture (First Touch + Last Touch)
 */
export type UtmKey = 'utm_source' | 'utm_medium' | 'utm_campaign' | 'utm_content' | 'utm_term' | 'utm_matchtype' | 'utm_device' | 'utm_network' | 'utm_placement' | 'utm_test';

export type UtmFieldState = {
	value: string | null
	capturedAtMs: number | null
};

export type UtmAttributionState = {
	version: 1
	first: Record<UtmKey, UtmFieldState>
	last: Record<UtmKey, UtmFieldState>
};

const UTM_ATTRIBUTION_STORAGE_KEY = 'rd_utm_attribution_v1';
const UTM_KEYS: UtmKey[] = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_matchtype', 'utm_device', 'utm_network', 'utm_placement', 'utm_test'];

const emptyUtmState = (): UtmAttributionState => ({
	version: 1,
	first: {
		utm_source: { value: null, capturedAtMs: null },
		utm_medium: { value: null, capturedAtMs: null },
		utm_campaign: { value: null, capturedAtMs: null },
		utm_content: { value: null, capturedAtMs: null },
		utm_term: { value: null, capturedAtMs: null },
		utm_matchtype: { value: null, capturedAtMs: null },
		utm_device: { value: null, capturedAtMs: null },
		utm_network: { value: null, capturedAtMs: null },
		utm_placement: { value: null, capturedAtMs: null },
		utm_test: { value: null, capturedAtMs: null },
	},
	last: {
		utm_source: { value: null, capturedAtMs: null },
		utm_medium: { value: null, capturedAtMs: null },
		utm_campaign: { value: null, capturedAtMs: null },
		utm_content: { value: null, capturedAtMs: null },
		utm_term: { value: null, capturedAtMs: null },
		utm_matchtype: { value: null, capturedAtMs: null },
		utm_device: { value: null, capturedAtMs: null },
		utm_network: { value: null, capturedAtMs: null },
		utm_placement: { value: null, capturedAtMs: null },
		utm_test: { value: null, capturedAtMs: null },
	},
});

/*
 * Gets stored UTM attribution state from localStorage.
 */
export const getStoredUtmAttributionState = (): UtmAttributionState => {
	if (typeof window === 'undefined') return emptyUtmState();
	const stored = safeJsonParse<UtmAttributionState>(window.localStorage.getItem(UTM_ATTRIBUTION_STORAGE_KEY));
	if (!stored || stored.version !== 1) return emptyUtmState();
	return stored;
};

/*
 * Parses UTM parameters from current URL.
 */
const getUtmFromUrl = (): Partial<Record<UtmKey, string>> => {
	if (typeof window === 'undefined') return {};
	const params = new URLSearchParams(window.location.search);

	const out: Partial<Record<UtmKey, string>> = {};
	UTM_KEYS.forEach(k => {
		const v = params.get(k);
		if (v && v.trim()) out[k] = v.trim();
	});
	return out;
};

/*
 * Persists UTM attribution from URL to localStorage.
 * - First Touch: set-once per field (only if empty)
 * - Last Touch: always overwrite per field
 * Call this once at app boot (e.g., in App.vue created()).
 * Returns the current state of UTM attribution after potential update.
 */
export const persistUtmAttributionFromUrl = (): UtmAttributionState => {
	if (typeof window === 'undefined') return emptyUtmState();

	const fromUrl = getUtmFromUrl();
	const keys = Object.keys(fromUrl) as UtmKey[];
	if (!keys.length) return getStoredUtmAttributionState();

	const existing = getStoredUtmAttributionState();
	const now = Date.now();

	const next: UtmAttributionState = {
		version: 1,
		first: { ...existing.first },
		last: { ...existing.last },
	};

	keys.forEach(k => {
		const v = fromUrl[k] || null;
		if (!v) return;

		// last-touch: always overwrite (per-field)
		next.last[k] = { value: v, capturedAtMs: now };

		// first-touch: set once (per-field)
		if (!next.first[k]?.value) {
			next.first[k] = { value: v, capturedAtMs: now };
		}
	});

	window.localStorage.setItem(UTM_ATTRIBUTION_STORAGE_KEY, JSON.stringify(next));
	return next;
};

const pushGtmDataLayerEvent: Function = (dataLayerPayload: any): void => {
	if (typeof window === 'undefined' || !(window as any).dataLayer) {
		console.error('Google Tag Manager dataLayer not found');
		return;
	}
	if (process.env.NODE_ENV !== 'production') {
		// Only track on prod
		return;
	}
	(window as any).dataLayer.push(dataLayerPayload);
};

/*
 * Gets the Google Analytics client ID, Google Ads gclid, and other tracking cookies from browser.
 * Now includes raw click IDs (gclidRaw, gbraid, wbraid) for offline conversion tracking.
 */
export const getGtmCookies: Function = (userId: string): {
	clientId: string
	sessionId: string
	gclid: string // Original _gcl_aw cookie value (wrapper format)
	gclidRaw: string // Raw GCLID extracted from URL or parsed from cookie
	gbraid: string // iOS app attribution
	wbraid: string // Web-to-app attribution
	gclidCapturedAtMs: number // Timestamp when click ID was captured
	sessionAttributesEncoded: string // Google Ads session attributes
	sessionAttributesCapturedAtMs: number // Timestamp when session attributes were captured
	fbp: string
	hubspotutk: string
} | null => {
	if (typeof window === 'undefined') {
		return null;
	}

	// Check for existing GA cookies
	const clientId: string = getCookie('_ga') || null;
	const sessionId: string = getCookie(`_ga_${GA_MEASUREMENT_ID.replace('G-', '')}`) || null;
	const gclid: string = getCookie('_gcl_aw') || null;
	const fbp: string = getCookie('_fbp') || null;
	const hubspotutk: string = getCookie('hubspotutk') || null;

	// Get raw click IDs (URL > localStorage > cookie)
	const adsClickIds = getGoogleAdsClickIds();

	// Get session attributes (localStorage)
	const sessionAttrs = getStoredSessionAttributes();

	return {
		clientId: clientId || null,
		sessionId: sessionId || null,
		gclid: gclid || null, // Keep original for backwards compatibility
		gclidRaw: adsClickIds.gclid || null,
		gbraid: adsClickIds.gbraid || null,
		wbraid: adsClickIds.wbraid || null,
		gclidCapturedAtMs: adsClickIds.capturedAtMs || null,
		sessionAttributesEncoded: sessionAttrs.sessionAttributesEncoded || null,
		sessionAttributesCapturedAtMs: sessionAttrs.capturedAtMs || null,
		fbp: fbp || null,
		hubspotutk: hubspotutk || null,
	};
};

/*
 * Pushes a purchase event to the Google Tag Manager dataLayer
 */
export const trackGtmPurchase = (
	paymentId: string,
	valueInCents: number,
	currency: string,
	items: TrackingPurchaseRevenueItem[],
): void => {
	if (valueInCents <= 0) return;

	pushGtmDataLayerEvent({ ecommerce: null }); // Clear the previous ecommerce object
	pushGtmDataLayerEvent({
		event: 'purchase',
		ecommerce: {
			transaction_id: paymentId,
			value: centsToDollars(valueInCents), // Sum of (price * quantity) for all items
			currency: (currency || 'usd').toLowerCase(),
			items: items || [],
			// tax: 3.60,
			// shipping: 5.99,
			// coupon: 'SUMMER_SALE',
			// items: []
			//   item_id: 'SKU_12345',
			//   item_name: 'Stan and Friends Tee',
			//   affiliation: 'Google Merchandise Store',
			//   coupon: 'SUMMER_FUN',
			//   discount: 2.22,
			//   index: 0,
			//   item_brand: 'Google',
			//   item_category: 'Apparel',
			//   item_category2: 'Adult',
			//   item_category3: 'Shirts',
			//   item_category4: 'Crew',
			//   item_category5: 'Short sleeve',
			//   item_list_id: 'related_products',
			//   item_list_name: 'Related Products',
			//   item_variant: 'green',
			//   location_id: 'ChIJIQBpAG2ahYAR_6128GcTUEo',
			//   price: 10.01,
			//   quantity: 3,
		},
	});
};

/*
 * Pushes a generation event to the Google Tag Manager dataLayer
 */
export const trackGtmRunGeneration = (user: User, nodeDef: RunnitNodeDef): void => {
	if (!user || !user.activatedAt || !nodeDef) return;
	const nowMoment: Moment = moment();
	const userActivatedAtMoment: Moment = moment(user.activatedAt.toMillis());

	// Calculate weeks and months since activation, floored to integers
	const daysSinceActivation: number = Math.floor(nowMoment.diff(userActivatedAtMoment, 'days'));
	const weeksSinceActivation: number = Math.floor(nowMoment.diff(userActivatedAtMoment, 'weeks'));
	const monthsSinceActivation: number = Math.floor(nowMoment.diff(userActivatedAtMoment, 'months'));

	pushGtmDataLayerEvent({
		event: 'generate',
		event_generate: {
			is_model: nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER ? 1 : 0,
			returning_days: daysSinceActivation,
			returning_weeks: weeksSinceActivation,
			returning_months: monthsSinceActivation,
		},
	});
};

/*
 * LinkedIn Insight Tag conversion ID for the "Free Account Signup" conversion.
 * The Insight Tag base code itself is loaded via Google Tag Manager (GTM-P9RJXRG),
 * which defines window.lintrk in production. This is just the per-conversion ID.
 */
export const LINKEDIN_FREE_SIGNUP_CONVERSION_ID = 28099114;

const LINKEDIN_SIGNUP_CONVERSION_STORAGE_KEY = 'rd_linkedin_signup_conversion_tracked_v1';

/*
 * Fires the LinkedIn "Free Account Signup" conversion via the Insight Tag.
 *
 * Unlike the HubSpot signup conversion (deduped on the backend), window.lintrk
 * has no server-side de-duplication, so calling it on every login would inflate
 * the count. We de-dupe per userId in localStorage so it fires at most once per
 * account (per browser). Callers are responsible for the "brand new, confirmed
 * account" gating (email verified, recent signup).
 *
 * - Only runs in production (window.lintrk is only injected by GTM on prod).
 * - No-ops (and does NOT mark as tracked) if the Insight Tag isn't ready yet,
 *   so a later call can retry.
 *
 * Returns true only if the conversion was actually fired.
 */
export const trackLinkedInFreeSignupConversion = (userId: string): boolean => {
	if (typeof window === 'undefined' || !userId) return false;
	if (process.env.NODE_ENV !== 'production') return false;

	// De-dupe: only fire once per user (per browser).
	let tracked: Record<string, boolean> = safeJsonParse<Record<string, boolean>>(
		window.localStorage.getItem(LINKEDIN_SIGNUP_CONVERSION_STORAGE_KEY),
	) || {};
	if (tracked[userId]) return false;

	const lintrk = (window as any).lintrk;
	if (typeof lintrk !== 'function') {
		// Insight Tag (loaded via GTM) not ready yet — don't mark as tracked so we can retry later.
		return false;
	}

	try {
		lintrk('track', { conversion_id: LINKEDIN_FREE_SIGNUP_CONVERSION_ID });
		tracked = { ...tracked, [userId]: true };
		window.localStorage.setItem(LINKEDIN_SIGNUP_CONVERSION_STORAGE_KEY, JSON.stringify(tracked));
		return true;
	} catch (e) {
		console.error('Error firing LinkedIn signup conversion:', e);
		return false;
	}
};
